import { z } from 'zod';

import { BaseToolResultSchema } from './baseResult';

const MAX_REGIONS_LIMIT = 100;
const DEFAULT_MAX_REGIONS = 10;

export const DetectTextArgsSchema = z.object({
  imageUrl: z.string().describe('Image source — URL, local file path, or base64 data URL'),
  includeWords: z
    .boolean()
    .default(false)
    .describe('When true, each region also includes word-level bounding boxes, in reading order.'),
  maxRegions: z
    .number()
    .int()
    .min(1)
    .max(MAX_REGIONS_LIMIT)
    .default(DEFAULT_MAX_REGIONS)
    .describe('Maximum number of text regions to return, ordered largest-bounding-box first.'),
});

export const DetectTextWordSchema = z.object({
  boundingBox: z.object({
    height: z.number(),
    width: z.number(),
    x: z.number(),
    y: z.number(),
  }),
  text: z.string(),
});

export const DetectTextRegionSchema = z.object({
  boundingBox: z.object({
    height: z.number(),
    width: z.number(),
    x: z.number(),
    y: z.number(),
  }),
  text: z.string(),
  // Present only when the caller requests `includeWords` — omitted by older cached results and
  // absent entirely until the backend that returns it has shipped.
  words: z.array(DetectTextWordSchema).optional(),
});

export type DetectTextRegion = z.infer<typeof DetectTextRegionSchema>;

export const DetectTextResultSchema = BaseToolResultSchema.extend({
  imageDimensions: z.object({ height: z.number(), width: z.number() }),
  message: z.string(),
  regions: z.array(DetectTextRegionSchema),
});

export type DetectTextResult = z.infer<typeof DetectTextResultSchema>;

export const DETECT_TEXT_TOOL_NAME = 'detect_text';

export const DETECT_TEXT_TOOL_DESCRIPTION = 'Detect all text in an image using OCR.';

export const DETECT_TEXT_TOOL = {
  description: DETECT_TEXT_TOOL_DESCRIPTION,
  name: DETECT_TEXT_TOOL_NAME,
  parameters: DetectTextArgsSchema.toJSONSchema(),
};
